import { useState } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  getSubsystem, updateSubsystem, deleteSubsystem,
  SubsystemStatus, STATUS_DEFINITIONS 
} from '../../api/subsystems'
import { getProject } from '../../api/projects'
import { getTasks, createTask, TaskItem } from '../../api/tasks'
import { getProjectNetworks } from '../../api/commNetworks'
import PostsSection from '../Posts/PostsSection'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { 
  BookOpen, CheckSquare, Trash2, Edit3, Key, FileText, Phone, 
  Eye, EyeOff, Plus, Network, Layers
} from 'lucide-react'

const formatDateTime = (value?: string | null) => {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

export default function SubsystemViewPage() {
  const { projectId, subsystemId } = useParams<{ projectId: string; subsystemId: string }>()
  const projectIdNum = Number(projectId)
  const subsystemIdNum = Number(subsystemId)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const activeTab = searchParams.get('tab') || 'passport'
  const setActiveTab = (tab: string) => {
    setSearchParams({ tab }, { replace: true })
  }

  // Modals & form state
  const [showEditModal, setShowEditModal] = useState(false)
  const [editName, setEditName] = useState('')
  const [editStatus, setEditStatus] = useState<SubsystemStatus>('operational')
  const [editDescription, setEditDescription] = useState('')
  const [editContractInfo, setEditContractInfo] = useState('')
  const [editCredentialsInfo, setEditCredentialsInfo] = useState('')
  const [editContactsInfo, setEditContactsInfo] = useState('')

  // Quick task creation modal inside subsystem
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [newTaskDescription, setNewTaskDescription] = useState('')
  const [newTaskDeadline, setNewTaskDeadline] = useState('')

  // Passwords visibility toggle
  const [showPasswords, setShowPasswords] = useState(false)

  // Load project
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectIdNum),
    enabled: !!projectIdNum,
  })

  // Load subsystem
  const { data: subsystem, isLoading: isSubsystemLoading } = useQuery({
    queryKey: ['subsystem', subsystemIdNum],
    queryFn: () => getSubsystem(subsystemIdNum),
    enabled: !!subsystemIdNum,
  })

  // Load tasks for this project
  const { data: tasks = [], isLoading: isTasksLoading } = useQuery({
    queryKey: ['tasks', projectIdNum, subsystemIdNum],
    queryFn: () => getTasks(projectIdNum, subsystemIdNum),
    enabled: !!projectIdNum && !!subsystemIdNum,
  })

  // Load project networks for relations
  const { data: networks = [] } = useQuery({
    queryKey: ['projectNetworks', projectIdNum],
    queryFn: () => getProjectNetworks(projectIdNum),
    enabled: !!projectIdNum,
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => updateSubsystem(subsystemIdNum, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subsystem', subsystemIdNum] })
      queryClient.invalidateQueries({ queryKey: ['subsystems', projectIdNum] })
      setShowEditModal(false)
    },
    onError: (err: any) => {
      alert(err.response?.data?.error || 'Ошибка при сохранении данных подсистемы')
    }
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => deleteSubsystem(subsystemIdNum),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subsystems', projectIdNum] })
      navigate(`/projects/${projectId}/systems`)
    },
    onError: (err: any) => {
      alert(err.response?.data?.error || 'Ошибка при удалении подсистемы')
    }
  })

  // Create task mutation
  const createTaskMutation = useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', projectIdNum, subsystemIdNum] })
      queryClient.invalidateQueries({ queryKey: ['subsystem', subsystemIdNum] })
      setShowTaskModal(false)
      setNewTaskTitle('')
      setNewTaskDescription('')
      setNewTaskDeadline('')
    },
    onError: (err: any) => {
      alert(err.response?.data?.error || 'Ошибка при создании задачи')
    }
  })

  const openEditModal = () => {
    if (!subsystem) return
    setEditName(subsystem.name)
    setEditStatus(subsystem.status)
    setEditDescription(subsystem.description || '')
    setEditContractInfo(subsystem.contract_info || '')
    setEditCredentialsInfo(subsystem.credentials_info || '')
    setEditContactsInfo(subsystem.contacts_info || '')
    setShowEditModal(true)
  }

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateMutation.mutate({
      name: editName.trim(),
      status: editStatus,
      description: editDescription.trim() || null,
      contract_info: editContractInfo.trim() || null,
      credentials_info: editCredentialsInfo.trim() || null,
      contacts_info: editContactsInfo.trim() || null,
    })
  }

  const handleStatusChange = (newSt: SubsystemStatus) => {
    updateMutation.mutate({ status: newSt })
  }

  const handleDelete = () => {
    if (confirm(`Удалить подсистему «${subsystem?.name}» и весь её бортжурнал? Это действие необратимо.`)) {
      deleteMutation.mutate()
    }
  }

  const handleCreateTask = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTaskTitle.trim()) return
    createTaskMutation.mutate({
      project_id: projectIdNum,
      subsystem_id: subsystemIdNum,
      title: newTaskTitle.trim(),
      description: newTaskDescription.trim() || undefined,
      deadline: newTaskDeadline || undefined,
      status: 'new'
    })
  }

  if (isSubsystemLoading) {
    return <div className="text-center py-16 text-gray-500">Загрузка подсистемы...</div>
  }

  if (!subsystem) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200 p-8">
        <h3 className="text-lg font-bold text-red-600">Подсистема не найдена</h3>
        <Link to={`/projects/${projectId}/systems`} className="mt-3 inline-block text-blue-600 hover:underline">
          ← Вернуться к списку инженерных систем
        </Link>
      </div>
    )
  }

  const statusDef = STATUS_DEFINITIONS[subsystem.status] || STATUS_DEFINITIONS.operational

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
          <Link to={`/projects/${projectId}`} className="hover:text-blue-600">
            {project?.name || 'Проект'}
          </Link>
          <span>/</span>
          <Link to={`/projects/${projectId}/systems`} className="hover:text-blue-600">
            Инженерные системы
          </Link>
          <span>/</span>
          <span className="text-gray-800 font-semibold">{subsystem.name}</span>
        </div>

        {/* Title & Quick Actions */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl border border-blue-150">
              <Layers className="w-7 h-7" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Инженерная система
                </span>
                <span className="text-gray-300">•</span>
                <span className="text-xs text-gray-400 font-mono">ID #{subsystem.id}</span>
              </div>
              <h1 className="text-2xl font-extrabold text-gray-900 mt-0.5">{subsystem.name}</h1>
              {subsystem.description && (
                <p className="text-sm text-gray-600 mt-1 max-w-3xl">{subsystem.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {/* Quick Status Dropdown */}
            <select
              value={subsystem.status}
              onChange={(e) => handleStatusChange(e.target.value as SubsystemStatus)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${statusDef.badgeClass} focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer`}
            >
              {Object.entries(STATUS_DEFINITIONS).map(([st, def]) => (
                <option key={st} value={st}>{def.label}</option>
              ))}
            </select>

            <button
              onClick={openEditModal}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-xs font-semibold transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              Редактировать
            </button>

            <button
              onClick={handleDelete}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Удалить подсистему"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-2 border-t border-gray-150 mt-6 pt-4">
          <button
            onClick={() => setActiveTab('passport')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors ${
              activeTab === 'passport'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FileText className="w-4 h-4" />
            Паспорт и реквизиты
          </button>

          <button
            onClick={() => setActiveTab('logbook')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors ${
              activeTab === 'logbook'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Бортжурнал ({subsystem.post_count || 0})
          </button>

          <button
            onClick={() => setActiveTab('tasks')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors ${
              activeTab === 'tasks'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <CheckSquare className="w-4 h-4" />
            Задачи ({tasks.length})
          </button>

          <button
            onClick={() => setActiveTab('relations')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors ${
              activeTab === 'relations'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Network className="w-4 h-4" />
            Связи с сетями
          </button>
        </div>
      </div>

      {/* Tab 1: Passport (Cards for contracts, credentials, contacts) */}
      {activeTab === 'passport' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Card 1: Contracts & Providers */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                <div className="flex items-center gap-2 text-amber-800 font-bold text-sm">
                  <FileText className="w-4 h-4 text-amber-600" />
                  <span>Договоры и тарифы</span>
                </div>
                <button
                  onClick={openEditModal}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Изменить
                </button>
              </div>

              {subsystem.contract_info ? (
                <div className="prose prose-sm max-w-none text-gray-700 text-xs leading-relaxed bg-amber-50/50 p-3 rounded-lg border border-amber-100">
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                    {subsystem.contract_info}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="text-xs text-gray-400 italic py-4 text-center">
                  Реквизиты договоров, лицевые счета и тарифы пока не заполнены.
                </div>
              )}
            </div>

            {/* Card 2: Credentials & Access */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                <div className="flex items-center gap-2 text-purple-800 font-bold text-sm">
                  <Key className="w-4 h-4 text-purple-600" />
                  <span>Доступы и учетные записи</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
                    title={showPasswords ? 'Скрыть пароли' : 'Показать пароли'}
                  >
                    {showPasswords ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                  <button
                    onClick={openEditModal}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Изменить
                  </button>
                </div>
              </div>

              {subsystem.credentials_info ? (
                <div className="prose prose-sm max-w-none text-gray-700 text-xs leading-relaxed bg-purple-50/50 p-3 rounded-lg border border-purple-100 font-mono">
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                    {showPasswords ? subsystem.credentials_info : subsystem.credentials_info.replace(/password:?\s*\S+/gi, 'password: ••••••••')}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="text-xs text-gray-400 italic py-4 text-center">
                  Логины, пароли, статические IP-адреса и ключи пока не сохранены.
                </div>
              )}
            </div>

            {/* Card 3: Contacts & Service */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                <div className="flex items-center gap-2 text-blue-800 font-bold text-sm">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <span>Контакты и сервис</span>
                </div>
                <button
                  onClick={openEditModal}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Изменить
                </button>
              </div>

              {subsystem.contacts_info ? (
                <div className="prose prose-sm max-w-none text-gray-700 text-xs leading-relaxed bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                    {subsystem.contacts_info}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="text-xs text-gray-400 italic py-4 text-center">
                  Контакты техподдержки, дежурных мастеров и аварийных служб пока не указаны.
                </div>
              )}
            </div>
          </div>

          {/* Quick banner about the Subsystem Logbook */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-3xl">📖</span>
              <div>
                <h4 className="text-sm font-bold text-blue-950">Бортжурнал обслуживания этой подсистемы</h4>
                <p className="text-xs text-blue-700 mt-0.5">
                  Фиксируйте визиты мастеров, замеры скоростей, заправки фреона, ремонты и прикрепляйте акты с фотографиями.
                </p>
              </div>
            </div>
            <button
              onClick={() => setActiveTab('logbook')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors shrink-0 shadow-xs"
            >
              Перейти в бортжурнал →
            </button>
          </div>
        </div>
      )}

      {/* Tab 2: Subsystem Logbook (Posts & attachments dedicated to this subsystem!) */}
      {activeTab === 'logbook' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5 text-xs text-blue-900">
              <BookOpen className="w-4 h-4 text-blue-600" />
              <span>
                <strong>Бортжурнал подсистемы «{subsystem.name}».</strong> Хроника событий, ремонтов, визитов инженеров, замеров и актов с фото.
              </span>
            </div>
          </div>

          {/* Dedicated posts for this subsystem */}
          <PostsSection
            entityType="subsystem"
            entityId={subsystemIdNum}
            canEdit={true}
          />
        </div>
      )}

      {/* Tab 3: Tasks */}
      {activeTab === 'tasks' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Задачи по подсистеме ({tasks.length})</h3>
              <p className="text-xs text-gray-500">Поручения и регламентные дела, привязанные к этому разделу объекта</p>
            </div>
            <button
              onClick={() => setShowTaskModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Новая задача
            </button>
          </div>

          {isTasksLoading ? (
            <div className="text-center py-8 text-gray-500">Загрузка задач...</div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="text-4xl mb-2">📋</div>
              <h4 className="text-sm font-bold text-gray-800">Нет активных задач по этой подсистеме</h4>
              <p className="text-xs text-gray-500 mt-1">Создайте задачу (например, «Оформить договор со вторым провайдером» или «Купить фильтры»).</p>
              <button
                onClick={() => setShowTaskModal(true)}
                className="mt-3 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold"
              >
                + Создать задачу
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {tasks.map((task: TaskItem) => (
                <div
                  key={task.id}
                  className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-2.5 h-2.5 rounded-full ${
                      task.status === 'completed' ? 'bg-emerald-500' :
                      task.status === 'in_progress' ? 'bg-blue-500' :
                      task.status === 'cancelled' ? 'bg-gray-400' : 'bg-amber-500'
                    }`} />
                    <div>
                      <Link
                        to={`/projects/${projectId}/tasks/${task.id}`}
                        className="text-sm font-bold text-gray-900 hover:text-blue-600 transition-colors"
                      >
                        {task.title}
                      </Link>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        {task.deadline && (
                          <span>Срок: {formatDateTime(task.deadline)}</span>
                        )}
                        {task.assignee_names && (
                          <span>Исполнитель: {task.assignee_names}</span>
                        )}
                        {task.checklist_total ? (
                          <span>Чеклист: {task.checklist_completed}/{task.checklist_total}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <Link
                    to={`/projects/${projectId}/tasks/${task.id}`}
                    className="text-xs text-blue-600 hover:underline font-semibold shrink-0"
                  >
                    Открыть →
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Relations */}
      {activeTab === 'relations' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">Связанные коммуникационные сети</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Сети проекта (LAN, Modbus, Audio), относящиеся к инфраструктуре этого объекта
            </p>
          </div>

          {networks.length === 0 ? (
            <div className="text-xs text-gray-500 py-4">В проекте пока не созданы коммуникационные сети.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {networks.map((net) => (
                <div key={net.id} className="p-3.5 border rounded-lg bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="text-lg">🌐</span>
                    <div>
                      <div className="text-sm font-bold text-gray-900">{net.name}</div>
                      <div className="text-xs text-gray-500">{net.network_type} • код: {net.code}</div>
                    </div>
                  </div>
                  <Link
                    to={`/projects/${projectId}/networks/${net.id}/interfaces`}
                    className="text-xs text-blue-600 hover:underline font-semibold"
                  >
                    Интерфейсы →
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Subsystem Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-4 border border-gray-150 my-8">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="text-lg font-bold text-gray-900">Редактировать паспорт подсистемы</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold p-1"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Название системы</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Статус</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as SubsystemStatus)}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {Object.entries(STATUS_DEFINITIONS).map(([st, def]) => (
                    <option key={st} value={st}>{def.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Описание / Оборудование</label>
                <textarea
                  rows={2}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Договоры, тарифы, лицевые счета (Markdown)
                </label>
                <textarea
                  rows={3}
                  value={editContractInfo}
                  onChange={(e) => setEditContractInfo(e.target.value)}
                  placeholder="Провайдер 1, Договор №..., Л/С..., Тариф..."
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Доступы, учетные записи, IP-адреса (Markdown)
                </label>
                <textarea
                  rows={3}
                  value={editCredentialsInfo}
                  onChange={(e) => setEditCredentialsInfo(e.target.value)}
                  placeholder="login: ..., password: ..., IP: ..."
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Контакты сервисных служб и инженеров (Markdown)
                </label>
                <textarea
                  rows={3}
                  value={editContactsInfo}
                  onChange={(e) => setEditContactsInfo(e.target.value)}
                  placeholder="Мастер Алексей: +7-9xx-..., ТП провайдера: 8-800-..."
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 text-xs"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-150">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-5 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold shadow-sm"
                >
                  {updateMutation.isPending ? 'Сохранение...' : 'Сохранить изменения'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 border border-gray-150">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="text-base font-bold text-gray-900">
                Новая задача по подсистеме «{subsystem.name}»
              </h3>
              <button
                onClick={() => setShowTaskModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold p-1"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Название задачи <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="например, Оформить договор с резервным провайдером"
                  required
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Срок (дедлайн)</label>
                <input
                  type="date"
                  value={newTaskDeadline}
                  onChange={(e) => setNewTaskDeadline(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Описание задачи</label>
                <textarea
                  rows={3}
                  value={newTaskDescription}
                  onChange={(e) => setNewTaskDescription(e.target.value)}
                  placeholder="Детали, требования, контакты..."
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-150">
                <button
                  type="button"
                  onClick={() => setShowTaskModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createTaskMutation.isPending || !newTaskTitle.trim()}
                  className="px-5 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold shadow-sm disabled:opacity-50"
                >
                  {createTaskMutation.isPending ? 'Создание...' : 'Создать задачу'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
